import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import { refusalMessage } from '@/features/cases/lib/refusal'
import {
  toAllocationPayload,
  useAllocatePayment,
  useConfirmPayment,
} from '@/features/finance/api/useFinance'
import { PaymentAllocationSheet } from '@/features/finance/components/PaymentAllocationSheet'
import { PaymentAmendSheet } from '@/features/finance/components/PaymentAmendSheet'
import { RejectPaymentSheet } from '@/features/finance/components/RejectPaymentSheet'
import { useAllocationTargets } from '@/features/finance/components/useAllocationTargets'
import { paymentRefusalMessage } from '@/features/finance/paymentRefusal'
import { CAP, can } from '@/lib/can'
import type { Payment } from '@/types/domain'

/* One payment's acts, compact enough for a row of a dense table (S-11
 * revision: «Платежи поставщикам» on «Затраты»).
 *
 * The same four fates the payment's other surfaces offer, through the same
 * mutations and the same sheets, so the wording and the rules cannot drift
 * between the places a payment is decided:
 *
 *  · «Подтвердить», «Исправить» and «Отклонить» — a PENDING payment's three
 *    fates (0059): confirming for a holder of `finance.payment.confirm`,
 *    amending and rejecting by the server's verdicts (`canAmend`,
 *    `canDelete`, S-21). Confirming moves no figure (L4), rejecting deletes
 *    the row with a mandatory reason (L8);
 *  · «Разнести» — by the server's `canAllocate`, and only while the
 *    payment has an unallocated remainder: on a fully allocated payment the
 *    act would only ever be refused, and a refused button on every row of a
 *    ledger is noise. Its targets are `useAllocationTargets`', the allocation
 *    block's own.
 *
 * Each act is ABSENT rather than refused for a reader without its capability:
 * the server re-checks it on the request (AD-21), and an act that could only
 * end in a 403 is not offered (QW-7). A refused confirm is said here, in the
 * reader's language, because there is no sheet holding a draft for it. */
export function PaymentActs({ payment }: { payment: Payment }) {
  const { t } = useTranslation()
  const { data: user } = useSession()
  const confirm = useConfirmPayment()
  const allocate = useAllocatePayment()
  const [open, setOpen] = useState<null | 'allocate' | 'amend' | 'reject'>(null)

  const mayConfirm = can(user, CAP.financePaymentConfirm)
  const mayAllocate = payment.canAllocate
  const { targets, blocked } = useAllocationTargets(payment, mayAllocate)

  const pending = payment.status === 'pending'
  const confirming = pending && mayConfirm
  const deciding = confirming || payment.canAmend || payment.canDelete
  /* An outbound payment's cost lines; the supplier ledger lists no other. */
  const allocating =
    mayAllocate && targets !== undefined && Number(payment.unallocated.amount) > 0

  if (!deciding && !allocating) return null

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        {confirming ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={confirm.isPending}
            onClick={() => confirm.mutate(payment.id)}
          >
            {t('finance.payments.confirm')}
          </Button>
        ) : null}
        {/* Named by the payment's reference: two rows of one ledger carry
            the same icons, and "Correct, Correct" tells a screen reader
            nothing about which record it is. */}
        {payment.canAmend ? (
          <button
            type="button"
            aria-label={t('finance.payments.amendAction', { reference: payment.reference })}
            className="text-text-muted hover:text-text-heading cursor-pointer p-1"
            onClick={() => setOpen('amend')}
          >
            <Pencil size={14} aria-hidden />
          </button>
        ) : null}
        {payment.canDelete ? (
          <button
            type="button"
            aria-label={t('finance.payments.rejectAction', { reference: payment.reference })}
            className="text-text-muted hover:text-negative cursor-pointer p-1"
            onClick={() => setOpen('reject')}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        ) : null}
        {allocating ? (
          <Button
            variant="secondary"
            size="sm"
            blockedReason={blocked}
            onClick={() => setOpen('allocate')}
          >
            {t('finance.allocations.allocate')}
          </Button>
        ) : null}
      </span>

      {confirm.error ? (
        <span role="alert" className="text-negative text-[12px] leading-snug">
          {refusalMessage(confirm.error, t)}
        </span>
      ) : null}

      {allocating && targets ? (
        <PaymentAllocationSheet
          open={open === 'allocate'}
          onClose={() => setOpen(null)}
          payment={payment}
          pending={allocate.isPending}
          /* Worded here, never `error.message` — the problem's `detail`,
             English written for a log (AD-27). */
          submitError={allocate.isError ? paymentRefusalMessage(allocate.error, t) : undefined}
          targets={targets}
          onSubmit={(splits) =>
            allocate.mutate(
              {
                paymentId: payment.id,
                payload: toAllocationPayload(splits, payment.amount.currency),
              },
              { onSuccess: () => setOpen(null) },
            )
          }
        />
      ) : null}
      {payment.canAmend ? (
        <PaymentAmendSheet
          open={open === 'amend'}
          onClose={() => setOpen(null)}
          payment={payment}
        />
      ) : null}
      {payment.canDelete ? (
        <RejectPaymentSheet
          open={open === 'reject'}
          onClose={() => setOpen(null)}
          payment={payment}
        />
      ) : null}
    </span>
  )
}
